import { routes } from '@/app/routes'
import { DsIcons, type DsIcon, type DsTone } from '@/ds'
import { notificationStrings as s } from '@/features/notifications/strings'
import type { AppNotification, NotificationData, ProfileType } from '@/types/models'
import { appLocale } from '@/i18n/locale.store'

export type NotificationBucket = 'rent' | 'lease' | 'agreement' | 'interest' | 'maintenance' | 'other'

export function notificationBucket(type: string): NotificationBucket {
  if (type.startsWith('rent.')) return 'rent'
  if (type.startsWith('lease.')) return 'lease'
  if (type.startsWith('agreement.')) return 'agreement'
  if (type.startsWith('property.interest.')) return 'interest'
  if (type.startsWith('maintenance.')) return 'maintenance'
  return 'other'
}

export const bucketDisplay: Record<NotificationBucket, { icon: DsIcon; tone: DsTone }> = {
  rent: { icon: DsIcons.wallet, tone: 'brand' },
  lease: { icon: DsIcons.building, tone: 'neutral' },
  agreement: { icon: DsIcons.document, tone: 'neutral' },
  interest: { icon: DsIcons.user, tone: 'neutral' },
  maintenance: { icon: DsIcons.wrench, tone: 'warning' },
  other: { icon: DsIcons.bell, tone: 'neutral' },
}

/** Null for an unknown screen or a row missing its id, so a newer backend cannot break an older client. */
export function notificationHref(data: NotificationData | undefined, role: ProfileType): string | null {
  if (!data?.screen) return null
  switch (data.screen) {
    case 'lease-detail':
    case 'payment-detail':
      // A payment is read in the context of its lease; a maintenance event opens that request on the Maintenance tab (LP-745).
      return data.leaseId ? `${routes.lease(data.leaseId)}${data.maintenanceId ? `?tab=maintenance&id=${encodeURIComponent(data.maintenanceId)}` : ''}` : null
    case 'agreement-detail':
      return data.leaseId ? routes.leaseAgreement(data.leaseId) : null
    case 'property-detail':
      // Interest acknowledged / declined go to the tenant, who sees the public listing.
      if (!data.propertyId) return null
      return role === 'tenant' ? routes.tenant.browseDetail(data.propertyId) : routes.landlord.property(data.propertyId)
    case 'payments':
      return role === 'tenant' ? routes.tenant.transactions : routes.landlord.transactions
    case 'property-interests':
      return routes.landlord.propertyInterests
    case 'tenant-dashboard':
      return routes.tenant.dashboard
    case 'support-request':
      // An admin's reply to, or resolution of, the user's own request (support.reply / support.resolved, LP-925); either side.
      return data.requestId ? routes.supportRequest(data.requestId) : null
    default:
      return null
  }
}

export const isToday = (iso: string, now = new Date()) => {
  const d = new Date(iso)
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()
}

const dmy = new Intl.DateTimeFormat(appLocale(), { day: 'numeric', month: 'short' })

/** "Just now" → "5m ago" → "3h ago" → "Yesterday" → "4d ago" → "12 Aug" (the mobile `fromNow()`). */
export function relativeTime(iso: string, now = new Date()): string {
  const diff = Math.max(0, now.getTime() - Date.parse(iso))
  const min = Math.floor(diff / 60_000)
  if (min < 1) return s.justNow
  if (min < 60) return s.minutesAgo(min)
  const hours = Math.floor(min / 60)
  if (hours < 24) return s.hoursAgo(hours)
  const days = Math.floor(hours / 24)
  if (days === 1) return s.yesterday
  if (days < 7) return s.daysAgo(days)
  return dmy.format(new Date(iso))
}

export function groupByDay(items: AppNotification[], now = new Date()): { title: string; items: AppNotification[] }[] {
  const today = items.filter((n) => isToday(n.createdAt, now))
  const earlier = items.filter((n) => !isToday(n.createdAt, now))
  return [
    ...(today.length ? [{ title: s.today, items: today }] : []),
    ...(earlier.length ? [{ title: s.earlier, items: earlier }] : []),
  ]
}

const longDay = new Intl.DateTimeFormat(appLocale(), { weekday: 'long', day: 'numeric', month: 'short' })
const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`

/** One group per calendar day, newest first: Today, Yesterday, then "Monday, 22 Sept". */
export function groupByDate(items: AppNotification[], now = new Date()): { title: string; items: AppNotification[] }[] {
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
  const groups: { key: string; title: string; items: AppNotification[] }[] = []
  for (const n of items) {
    const d = new Date(n.createdAt)
    const key = dayKey(d)
    let g = groups.find((x) => x.key === key)
    if (!g) {
      const title = key === dayKey(now) ? s.today : key === dayKey(yesterday) ? s.yesterday : longDay.format(d)
      g = { key, title, items: [] }
      groups.push(g)
    }
    g.items.push(n)
  }
  return groups.map(({ title, items }) => ({ title, items }))
}

export const NOTIFICATION_BUCKETS: NotificationBucket[] = ['rent', 'lease', 'agreement', 'interest', 'maintenance', 'other']

export const documentTitle = (unread: number, base = 'LeazePro') => (unread > 0 ? `(${unread > 99 ? '99+' : unread}) ${base}` : base)
